/**
 * Inventory Management Page
 *
 * Allows CRA Admins to assign analysts to institutions.
 * Each institution can have one primary analyst, but assignments can be shared.
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useRoleAssumption } from '../../../context/RoleAssumptionContext';
import {
  getUsersByOrg,
  getOrgAssignments,
  assignAnalystToInstitution,
  reassignInstitution,
  shareAssignmentWith,
  removeSharing,
  unassignInstitution,
  type UserProfile,
  type AnalystAssignment,
} from '../../../services/firestore';
import { getRatedInstitutions } from '../../../services/institutionService';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import './InventoryManagement.css';

interface InstitutionInfo {
  id: string;
  name: string;
}

interface AssignmentModalState {
  isOpen: boolean;
  institutionId: string | null;
  institutionName: string | null;
  currentAssignment: AnalystAssignment | null;
  mode: 'assign' | 'edit';
}

interface ShareModalState {
  isOpen: boolean;
  assignment: AnalystAssignment | null;
}

export const InventoryManagement: React.FC = () => {
  const { userProfile } = useAuth();
  const { effectiveOrganization, effectiveIsCRAAdmin, effectiveIsPlatformAdmin } = useRoleAssumption();

  const [assignments, setAssignments] = useState<AnalystAssignment[]>([]);
  const [analysts, setAnalysts] = useState<UserProfile[]>([]);
  const [institutions, setInstitutions] = useState<InstitutionInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  // Modal states
  const [assignModal, setAssignModal] = useState<AssignmentModalState>({
    isOpen: false,
    institutionId: null,
    institutionName: null,
    currentAssignment: null,
    mode: 'assign',
  });
  const [shareModal, setShareModal] = useState<ShareModalState>({
    isOpen: false,
    assignment: null,
  });

  const [selectedAnalyst, setSelectedAnalyst] = useState<string>('');
  const [shareAnalysts, setShareAnalysts] = useState<string[]>([]);

  // Filters
  const [filterAnalyst, setFilterAnalyst] = useState<string>('');
  const [filterAssigned, setFilterAssigned] = useState<string>('all'); // all, assigned, unassigned

  const canManage = effectiveIsCRAAdmin || effectiveIsPlatformAdmin;

  useEffect(() => {
    loadData();
  }, [effectiveOrganization?.id]);

  const loadData = async () => {
    if (!effectiveOrganization?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get institutions from BigQuery API - CRAs can see all institutions, Banks see only their access list
      const accessibleIds = effectiveOrganization.institutions_access || [];
      const isCRAOrg = effectiveOrganization.type === 'cra' || effectiveOrganization.type === 'platform';

      const ratedInstitutions = await getRatedInstitutions();
      const allInstitutions = ratedInstitutions
        .filter((inst) => {
          // CRA orgs can see all institutions (they rate all of them)
          if (isCRAOrg) return true;
          // Bank orgs only see institutions in their access list
          return accessibleIds.includes(inst.institution_id);
        })
        .map((inst) => ({
          id: inst.institution_id,
          name: inst.institution_name,
        }));
      setInstitutions(allInstitutions);

      // Get analysts in this org
      const users = await getUsersByOrg(effectiveOrganization.id);
      const analystUsers = users.filter(
        u => u.role === 'cra_analyst' && u.status === 'active'
      );
      setAnalysts(analystUsers);

      // Get current assignments
      const orgAssignments = await getOrgAssignments(effectiveOrganization.id);
      setAssignments(orgAssignments);
    } catch (err) {
      console.error('Error loading inventory data:', err);
      setError('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const getAssignmentForInstitution = (institutionId: string): AnalystAssignment | undefined => {
    return assignments.find(a => a.institution_id === institutionId);
  };

  const getAnalystName = (analystId: string): string => {
    const analyst = analysts.find(a => a.uid === analystId);
    return analyst?.display_name || analyst?.email || 'Unknown';
  };

  const openAssignModal = (institution: InstitutionInfo) => {
    const currentAssignment = getAssignmentForInstitution(institution.id);
    setAssignModal({
      isOpen: true,
      institutionId: institution.id,
      institutionName: institution.name,
      currentAssignment: currentAssignment || null,
      mode: currentAssignment ? 'edit' : 'assign',
    });
    setSelectedAnalyst(currentAssignment?.analyst_id || '');
  };

  const closeAssignModal = () => {
    setAssignModal({
      isOpen: false,
      institutionId: null,
      institutionName: null,
      currentAssignment: null,
      mode: 'assign',
    });
    setSelectedAnalyst('');
  };

  const openShareModal = (assignment: AnalystAssignment) => {
    setShareModal({ isOpen: true, assignment });
    setShareAnalysts(assignment.shared_with || []);
  };

  const closeShareModal = () => {
    setShareModal({ isOpen: false, assignment: null });
    setShareAnalysts([]);
  };

  const handleAssign = async () => {
    if (!assignModal.institutionId || !selectedAnalyst || !effectiveOrganization?.id) return;

    try {
      setProcessing(true);
      setError(null);

      const assignedBy = userProfile?.uid || 'unknown';

      if (assignModal.currentAssignment) {
        // Reassign
        await reassignInstitution(
          assignModal.institutionId,
          effectiveOrganization.id,
          selectedAnalyst,
          assignedBy
        );
      } else {
        // New assignment
        await assignAnalystToInstitution(
          selectedAnalyst,
          assignModal.institutionId,
          effectiveOrganization.id,
          assignedBy
        );
      }

      await loadData();
      closeAssignModal();
    } catch (err: any) {
      console.error('Error assigning analyst:', err);
      setError(err.message || 'Failed to assign analyst');
    } finally {
      setProcessing(false);
    }
  };

  const handleUnassign = async () => {
    if (!assignModal.currentAssignment) return;

    if (!confirm('Are you sure you want to unassign this analyst?')) return;

    try {
      setProcessing(true);
      await unassignInstitution(assignModal.currentAssignment.id);
      await loadData();
      closeAssignModal();
    } catch (err) {
      console.error('Error unassigning:', err);
      setError('Failed to unassign analyst');
    } finally {
      setProcessing(false);
    }
  };

  const handleShareUpdate = async () => {
    if (!shareModal.assignment) return;

    try {
      setProcessing(true);
      const currentShared = shareModal.assignment.shared_with || [];

      // Find analysts to add and remove
      const toAdd = shareAnalysts.filter(id => !currentShared.includes(id));
      const toRemove = currentShared.filter(id => !shareAnalysts.includes(id));

      // Add new shares
      if (toAdd.length > 0) {
        await shareAssignmentWith(shareModal.assignment.id, toAdd);
      }

      // Remove shares
      for (const id of toRemove) {
        await removeSharing(shareModal.assignment.id, id);
      }

      await loadData();
      closeShareModal();
    } catch (err) {
      console.error('Error updating shares:', err);
      setError('Failed to update sharing');
    } finally {
      setProcessing(false);
    }
  };

  const toggleShareAnalyst = (analystId: string) => {
    setShareAnalysts(prev =>
      prev.includes(analystId)
        ? prev.filter(id => id !== analystId)
        : [...prev, analystId]
    );
  };

  // Filter institutions
  const filteredInstitutions = institutions.filter(inst => {
    const assignment = getAssignmentForInstitution(inst.id);

    if (filterAssigned === 'assigned' && !assignment) return false;
    if (filterAssigned === 'unassigned' && assignment) return false;
    if (filterAnalyst && assignment?.analyst_id !== filterAnalyst) return false;

    return true;
  });

  if (!effectiveOrganization) {
    return (
      <div className="inventory-page">
        <h1>Analyst Inventory</h1>
        <div className="empty-state">
          <span className="empty-icon">🏢</span>
          <h3>No Organization Selected</h3>
          <p>Please select an organization to manage analyst assignments.</p>
        </div>
      </div>
    );
  }

  if (!canManage) {
    return (
      <div className="inventory-page">
        <h1>Analyst Inventory</h1>
        <div className="empty-state">
          <span className="empty-icon">🔒</span>
          <h3>Access Denied</h3>
          <p>Only CRA Admins can manage analyst assignments.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="inventory-page">
        <h1>Analyst Inventory</h1>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="inventory-page">
      <div className="inventory-header">
        <h1>Analyst Inventory</h1>
        <p className="inventory-subtitle">{effectiveOrganization.name}</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Stats */}
      <div className="inventory-stats">
        <div className="stat-card">
          <div className="stat-value">{institutions.length}</div>
          <div className="stat-label">Total Institutions</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{assignments.length}</div>
          <div className="stat-label">Assigned</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{institutions.length - assignments.length}</div>
          <div className="stat-label">Unassigned</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{analysts.length}</div>
          <div className="stat-label">Active Analysts</div>
        </div>
      </div>

      {/* Filters */}
      <div className="inventory-filters">
        <select
          value={filterAnalyst}
          onChange={e => setFilterAnalyst(e.target.value)}
          className="filter-select"
        >
          <option value="">All Analysts</option>
          {analysts.map(a => (
            <option key={a.uid} value={a.uid}>
              {a.display_name || a.email}
            </option>
          ))}
        </select>

        <select
          value={filterAssigned}
          onChange={e => setFilterAssigned(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Institutions</option>
          <option value="assigned">Assigned Only</option>
          <option value="unassigned">Unassigned Only</option>
        </select>
      </div>

      {/* Inventory Table */}
      <div className="inventory-table">
        <table>
          <thead>
            <tr>
              <th>Institution</th>
              <th>Assigned Analyst</th>
              <th>Shared With</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInstitutions.map(inst => {
              const assignment = getAssignmentForInstitution(inst.id);
              return (
                <tr key={inst.id}>
                  <td className="institution-cell">
                    <span className="institution-name">{inst.name}</span>
                    <span className="institution-id">{inst.id}</span>
                  </td>
                  <td>
                    {assignment ? (
                      <span className="analyst-badge assigned">
                        {getAnalystName(assignment.analyst_id)}
                      </span>
                    ) : (
                      <span className="analyst-badge unassigned">Unassigned</span>
                    )}
                  </td>
                  <td>
                    {assignment && assignment.shared_with.length > 0 ? (
                      <span className="shared-count">
                        {assignment.shared_with.length} analyst{assignment.shared_with.length !== 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span className="shared-none">-</span>
                    )}
                  </td>
                  <td className="actions-cell">
                    <button
                      className={assignment ? 'edit-btn' : 'assign-btn'}
                      onClick={() => openAssignModal(inst)}
                    >
                      {assignment ? 'Edit' : 'Assign'}
                    </button>
                    {assignment && (
                      <button
                        className="share-btn"
                        onClick={() => openShareModal(assignment)}
                      >
                        Share
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredInstitutions.length === 0 && (
          <div className="no-results">
            No institutions match your filters.
          </div>
        )}
      </div>

      {/* Assignment Modal */}
      {assignModal.isOpen && (
        <div className="modal-overlay" onClick={closeAssignModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{assignModal.mode === 'edit' ? 'Edit Assignment' : 'Assign Analyst'}</h2>
            <p className="modal-subtitle">{assignModal.institutionName}</p>

            <div className="form-group">
              <label htmlFor="analyst">Select Analyst</label>
              <select
                id="analyst"
                value={selectedAnalyst}
                onChange={e => setSelectedAnalyst(e.target.value)}
              >
                <option value="">Select an analyst...</option>
                {analysts.map(a => (
                  <option key={a.uid} value={a.uid}>
                    {a.display_name || a.email}
                  </option>
                ))}
              </select>
            </div>

            {analysts.length === 0 && (
              <div className="warning-message">
                No active analysts found in this organization.
              </div>
            )}

            <div className="modal-actions">
              {assignModal.currentAssignment && (
                <button
                  className="danger-btn"
                  onClick={handleUnassign}
                  disabled={processing}
                >
                  Unassign
                </button>
              )}
              <button className="cancel-btn" onClick={closeAssignModal} disabled={processing}>
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleAssign}
                disabled={!selectedAnalyst || processing}
              >
                {processing ? 'Saving...' : assignModal.mode === 'edit' ? 'Update' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {shareModal.isOpen && shareModal.assignment && (
        <div className="modal-overlay" onClick={closeShareModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Share Assignment</h2>
            <p className="modal-subtitle">
              {institutions.find(i => i.id === shareModal.assignment?.institution_id)?.name}
            </p>
            <p className="modal-info">
              Primary: {getAnalystName(shareModal.assignment.analyst_id)}
            </p>

            <div className="form-group">
              <label>Share with other analysts:</label>
              <div className="analyst-checkboxes">
                {analysts
                  .filter(a => a.uid !== shareModal.assignment?.analyst_id)
                  .map(a => (
                    <label key={a.uid} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={shareAnalysts.includes(a.uid)}
                        onChange={() => toggleShareAnalyst(a.uid)}
                      />
                      {a.display_name || a.email}
                    </label>
                  ))}
              </div>
              {analysts.filter(a => a.uid !== shareModal.assignment?.analyst_id).length === 0 && (
                <div className="warning-message">
                  No other analysts available to share with.
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={closeShareModal} disabled={processing}>
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleShareUpdate}
                disabled={processing}
              >
                {processing ? 'Saving...' : 'Update Sharing'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryManagement;
